import { Button, ClientLogo, OrganizationChartIcon, Text } from '@viapeople/components'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { sessionStore } from '../../sharedStores/sessionStore'
import { formatDate } from '../../utils/formatters'
import { trpc } from '../../utils/trpc'
import type { Organization, OrganizationSearchInput } from './types'

const PageHeader = ({ userName, createOrganization }: { userName: string; createOrganization?: () => void }) => {
	return (
		<>
			<div className='flex'>
				<Text variant='large'>
					Welcome,{' '}
					<Text className='inline' variant='large' weight='semibold'>
						{userName}
					</Text>
				</Text>
			</div>

			<div className='flex justify-between items-center h-12 mt-6 mb-6'>
				<Text variant='h3' weight='semibold'>
					Organizations
				</Text>
				{createOrganization && (
					<Button variant='primary' onClick={createOrganization}>
						Create Organization
					</Button>
				)}
			</div>
		</>
	)
}

export const OrganizationsPage = () => {
	const navigate = useNavigate()
	const sessionState = sessionStore.getState()

	const [isEmpty, setIsEmpty] = useState(false)
	const columns: Column<Organization>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			render: (org: Organization) => (
				<div className='flex items-center gap-x-3'>
					<ClientLogo url={org.logo} />
					<div>
						<Text variant='xsmall' weight='semibold' className='group-hover:text-primary-600'>
							{org.name}
						</Text>
					</div>
				</div>
			),
		},
		{ field: 'email', label: 'Email', colSpan: 2 },
		{
			field: 'createdAt',
			label: 'Created',
			render: (org: Organization) => <Text variant='xsmall'>{formatDate(org.createdAt)}</Text>,
		},
	]

	const handleOrganizationClick = (org: Organization) => {
		navigate(`/organizations/${org.id}`)
	}

	const handleCreateOrganization = () => {
		navigate('/organizations/create')
	}

	return (
		<main className='w-full p-6'>
			<PageHeader
				userName={`${sessionState.user?.firstName || ''} ${sessionState.user?.lastName || ''}`}
				createOrganization={!isEmpty ? handleCreateOrganization : undefined}
			/>
			<GenericTable<Organization, OrganizationSearchInput>
				columns={columns}
				onStatusChange={(isEmpty) => setIsEmpty(isEmpty)}
				queryOptionsGenerator={(options) =>
					trpc.admin.organizations.search.queryOptions(
						{
							...options,
						},
						{ placeholderData: (prev) => prev },
					)
				}
				idField='id'
				onRowClick={handleOrganizationClick}
				emptyState={{
					icon: OrganizationChartIcon,
					title: 'Organizations',
					description: 'Get started by creating an organization',
					actionLabel: 'Create Organization',
					onAction: handleCreateOrganization,
				}}
			/>
		</main>
	)
}
